export type Validatable = {
  ipAddress: string,
  port: string,
  transport: string
}

export function validateIpPortCombinations<T extends Validatable, U extends Validatable>(
  listeners: T[] | undefined,
  destinations: U[] | undefined,
): boolean {
  const listenersIpPort = listeners?.map(trim).filter(filterNotEmpty).map(mapIpPortTransport) ?? [];
  const destinationsIpPort = destinations?.map(trim).filter(filterNotEmpty).map(mapIpPortTransport) ?? [];

  const combinedIpPortList = [...listenersIpPort, ...destinationsIpPort];

  const hasDuplicatedIpPort = (new Set(combinedIpPortList)).size !== combinedIpPortList.length;

  return !hasDuplicatedIpPort;
}

export function validateCurrentIpPortCombination<T extends Validatable, U extends Validatable>(
  current: T | U | undefined,
  listeners: T[] | undefined,
  destinations: U[] | undefined,
): boolean {
  if (current == null) {
    return true;
  }

  const { ipAddress, port, transport } = trim(current);
  if (ipAddress === '' || port === '' || transport === '') {
    return true;
  }

  const currentIpPortTransport = mapIpPortTransport(trim(current));

  const listenersIpPortTransport = listeners?.map(trim).filter(filterNotEmpty).map(mapIpPortTransport) ?? [];

  const destinationsIpPortTransport = destinations?.map(trim).filter(filterNotEmpty).map(mapIpPortTransport) ?? [];

  const combinedIpPortTransportList = [...listenersIpPortTransport, ...destinationsIpPortTransport];

  const matchedIpPortTransport = combinedIpPortTransportList.filter((entry) => entry === currentIpPortTransport);

  // The combinedIpPortList will also include the current interface being validated,
  // so it will be a problem only if there are multiple entries for the same port:ip
  const hasDuplicatedIpPortTransport = matchedIpPortTransport.length > 1;

  return !hasDuplicatedIpPortTransport;
}

function trim<T extends Validatable>(entry: T): T {
  return {
    ...entry,
    transport: entry.transport?.trim(),
    ipAddress: entry.ipAddress?.trim(),
    port: entry.port?.trim(),
  };
}

function filterNotEmpty<T extends Validatable>({ ipAddress, port, transport }: T): boolean {
  return ipAddress !== '' && port !== '' && transport !== '';
}

function mapIpPortTransport<T extends Validatable>({ ipAddress, port, transport }: T): string {
  return `${ipAddress}:${port}:${transport}`;
}
